<template>
  <div v-if="false"></div>
</template>
<script setup>

import { ref, onMounted, onUnmounted } from 'vue'
import VectorTileLayer from 'ol/layer/VectorTile' // 2D Canvas Vector Renderer
import VectorTileSource from 'ol/source/VectorTile'


import MVTFormat from 'ol/format/MVT'
import TileState from 'ol/TileState'
// import { applyStyle } from 'ol-mapbox-style'
import { PMTiles } from 'pmtiles'                  // Only the official core package
import { PMTilesVectorSource } from 'ol-pmtiles' // The OpenLayers source




import { Style, Fill, Stroke, Text } from 'ol/style'
import { Circle, RegularShape } from 'ol/style.js';
import { Polygon } from 'ol/geom'

// A cache to prevent memory leaks and keep rendering smooth
const styleCache = {}

// Feature statistics for debugging
const featureStats = {
  layers: {}
}

const collectFeatureStats = (feature) => {
  const layerName = getLayerName(feature)
  const featureClass = getFeatureClass(feature)
  const featureSubclass = getFeatureSubclass(feature)
  
  if (!featureStats.layers[layerName]) {
    featureStats.layers[layerName] = { count: 0, classes: {} }
  }
  
  featureStats.layers[layerName].count++
  
  if (!featureStats.layers[layerName].classes[featureClass]) {
    featureStats.layers[layerName].classes[featureClass] = { count: 0, subclasses: {} }
  }
  
  featureStats.layers[layerName].classes[featureClass].count++
  
  if (featureSubclass) {
    if (!featureStats.layers[layerName].classes[featureClass].subclasses[featureSubclass]) {
      featureStats.layers[layerName].classes[featureClass].subclasses[featureSubclass] = 0
    }
    featureStats.layers[layerName].classes[featureClass].subclasses[featureSubclass]++
  }
}

const printFeatureTree = () => {
  console.log('\n=== PMTiles Vector Features Tree ===\n')
  
  Object.entries(featureStats.layers).forEach(([layerName, layerData]) => {
    console.log(`📦 ${layerName} (${layerData.count} features)`)
    
    Object.entries(layerData.classes).forEach(([className, classData]) => {
      console.log(`  ├─ ${className || '(no class)'} (${classData.count})`)
      
      Object.entries(classData.subclasses).forEach(([subclassName, subclassCount], index, entries) => {
        const isLast = index === entries.length - 1
        console.log(`  │  ${isLast ? '└─' : '├─'} ${subclassName} (${subclassCount})`)
      })
    })
    console.log('')
  })
}

const getFeatureLabel = (feature) => {
  return (
    feature.get('name') ||
    feature.get('name:latin') ||
    feature.get('NAME') ||
    feature.get('ref') ||
    feature.get('alt_name') ||
    feature.get('place') ||
    ''
  )
}

const getLayerName = (feature) => {
  return (
    feature.get('layer') ||
    feature.get('sourceLayer') ||
    feature.get('layerName') ||
    feature.get('type') ||
    ''
  )
}

const getFeatureClass = (feature) => {
  return feature.get('class') || feature.get('kind') || ''
}

const getFeatureSubclass = (feature) => {
  return feature.get('subclass') || feature.get('subkind') || ''
}

const osmStyleFunction = (feature, resolution) => {
  const layerName = getLayerName(feature)
  const featureClass = getFeatureClass(feature)
  const featureSubclass = getFeatureSubclass(feature)
  const labelText = getFeatureLabel(feature)

  // Collect stats
  collectFeatureStats(feature)

  const cacheKey = `${layerName}_${featureClass}_${featureSubclass}`
  const resolutionDependent = layerName === 'building'

  let baseStyle = !resolutionDependent ? styleCache[cacheKey] : null

  if (!baseStyle) {
    let style = null

    switch (layerName) {
      // WATER LAYERS
      case 'waterway':
        style = new Style({
          stroke: new Stroke({
            color: '#aad3df',
            width: featureClass === 'river' ? 2 : 1,
            lineCap: 'round',
            lineJoin: 'round'
          }),
          zIndex: 10
        })
        break

      case 'water':
      case 'water_name':
        style = new Style({
          fill: new Fill({ color: '#aad3df' })
        })
        break

      // LANDUSE & LANDCOVER
      case 'landuse':
        const landuseFillColor = {
          residential: '#f0f0f0',
          industrial: '#cccccc',
          commercial: '#e8d5e5',
          park: '#c2e9b4',
          grass: '#c2e9b4',
          cemetery: '#dcdcdc',
          retail: '#ffd9d9',
          farmland: '#8c715e',
          forest: '#add19e',
          greenfield: '#c2e9b4',
          brownfield: '#b8a89e'
        }[featureClass] || '#efefef'
        
        style = new Style({ fill: new Fill({ color: landuseFillColor }) })
        break

      case 'landcover':
        let landcoverColor = '#efefef'
        
        if (featureClass === 'ice') {
          landcoverColor = '#42d7f5'
        } else if (featureClass === 'rock') {
          const rockColors = {
            scree: '#356a75',
            bare_rock: '#999'
          }
          landcoverColor = rockColors[featureSubclass] || '#000'
        } else if (featureClass === 'wood') {
          landcoverColor = '#add19e'
        } else if (featureClass === 'grass') {
          const grassColors = {
            grassland: '#c2e9b4',
            fell: '#d4e5a0',
            meadow: '#c2e9b4',
            scrub: '#b8c56a',
            heath: '#cdb88a'
          }
          landcoverColor = grassColors[featureSubclass] || '#c2e9b4'
        } else if (featureClass === 'farmland') {
          landcoverColor = featureSubclass === 'vineyard' ? '#8b7355' : '#8c715e'
        } else if (featureClass === 'wetland') {
          landcoverColor = '#304f49'
        }
        
        style = new Style({ fill: new Fill({ color: landcoverColor }) })
        break

      // TRANSPORTATION
      case 'transportation':
      case 'transportation_name':
        let roadColor = '#ffffff'
        let roadWidth = 1
        let zIndex = 1

        // Motorways and trunk
        if (featureClass === 'motorway' || featureClass === 'trunk') {
          roadColor = '#e990a0'
          roadWidth = 3
          zIndex = 10
        }
        // Primary and secondary roads
        else if (featureClass === 'primary' || featureClass === 'secondary') {
          roadColor = '#fcd7a1'
          roadWidth = 2
          zIndex = 5
        }
        // Tertiary and local roads
        else if (featureClass === 'tertiary' || featureClass === 'residential') {
          roadColor = '#ffffcc'
          roadWidth = 1.5
          zIndex = 3
        }
        // Service and path
        else if (featureClass === 'service' || featureClass === 'path') {
          roadColor = '#efefef'
          roadWidth = 1
          zIndex = 1
        }
        // Rail
        else if (featureClass === 'rail' || featureClass === 'track') {
          roadColor = '#8b7355'
          roadWidth = 2
          zIndex = 8
        }

        style = new Style({
          stroke: new Stroke({
            color: roadColor,
            width: roadWidth,
            lineCap: 'round',
            lineJoin: 'round'
          }),
          zIndex: zIndex
        })
        break

      // BOUNDARIES
      case 'boundary':
        const boundaryWidth = featureClass ? 2 : 1
        style = new Style({
          stroke: new Stroke({
            color: '#9e9e9e',
            width: boundaryWidth,
            lineDash: [4, 4]
          })
        })
        break

      // BUILDINGS
      case 'building':
        if (resolution < 4) {
          style = new Style({
            fill: new Fill({ color: '#d9d0c9' }),
            stroke: new Stroke({ color: '#bca99a', width: 1 })
          })
        }
        break

      // POI & FEATURES
      case 'poi':
        style = new Style({
          image: new Circle({
            radius: 4,
            fill: new Fill({ color: '#ff6b6b' }),
            stroke: new Stroke({ color: '#990000', width: 1 })
          }),
          zIndex: 20
        })
        break

      case 'mountain_peak':
        style = new Style({
          image: new RegularShape({
            fill: new Fill({ color: '#8b4513' }),
            stroke: new Stroke({ color: '#5c2d0f', width: 1 }),
            points: 3,
            radius: 5,
            angle: 0
          }),
          zIndex: 20
        })
        break

      case 'aeroway':
        style = new Style({
          stroke: new Stroke({
            color: '#d0d0d0',
            width: 2,
            lineDash: [2, 2]
          })
        })
        break

      case 'place':
        // Place layer mostly for labels - no fill needed
        style = new Style({})
        break

      case 'housenumber':
        style = new Style({
          image: new Circle({
            radius: 2,
            fill: new Fill({ color: '#999999' })
          })
        })
        break

      default:
        style = new Style({})
        break
    }

    baseStyle = style || new Style()
    if (!resolutionDependent) {
      styleCache[cacheKey] = baseStyle
    }
  }

  // LABELS
  if (labelText && resolution < 18) {
    const labelStyle = baseStyle.clone()
    const geometryType = feature.getGeometry()?.getType()
    const placement = geometryType === 'LineString' || geometryType === 'MultiLineString' ? 'line' : 'point'

    labelStyle.setText(
      new Text({
        text: labelText,
        font: '14px Calibri, sans-serif',
        fill: new Fill({ color: '#222' }),
        stroke: new Stroke({ color: '#fff', width: 3 }),
        overflow: true,
        placement,
        maxAngle: 0.9,
        offsetY: placement === 'point' ? -12 : 0
      })
    )

    return labelStyle
  }

  return baseStyle
}


const props = defineProps(['layer', 'map'])
const instancedLayer = ref(null)

const removeLayer = () => {
  if (!instancedLayer.value) return
  const source = instancedLayer.value.getSource()
  if (source && typeof source.dispose === 'function') source.dispose()
  if (typeof instancedLayer.value.dispose === 'function') instancedLayer.value.dispose()
  props.map.removeLayer(instancedLayer.value)
}

const addLayer = () => {
  instancedLayer.value = new VectorTileLayer({
            declutter: true,
            background: "#d3db95",
            source: new PMTilesVectorSource({
              url: props.layer.url,
              attributions: [""]
            }),
            style: osmStyleFunction
          });

  
    props.map.addLayer(instancedLayer.value)
}




onMounted(() => {
  console.log("🧩 " + props?.layer?.id + " mounted")
  window.pmtilesTreeStats = printFeatureTree
  console.log('💡 Call window.pmtilesTreeStats() in console to print feature tree with stats')
  addLayer()
})

onUnmounted(() => {
  console.log("🧩 " + props?.layer?.id + " unmounted")
  removeLayer()
})
</script>
